import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReceivedReviewsTable from '@company/components/CommunicationHub/CommunicationHubTables/ReceivedReviewsTable';
import FilterBar from '@company/components/CommunicationHub/FilterBar/FilterBar';
import {
  dismissItemsFromNew,
  getReceivedReviews,
} from '@company/services/com-hub/api';
import ThPagination from '@components/ThPagination/ThPagination';
import ComHubSkeletonLoader from '@company/components/CommunicationHub/ComHubSkeletonLoader';
import {
  UserActionAssociatedTypeEnum,
  ComHubQueryParamsEnum,
  ComHubTabs,
  Review,
  WorkerModalParams,
} from '@company/types/comm-hub.type';
import QueryKeys from '@constants/queryKeys';
import {
  dismissItems,
  handleComHubSearch,
  extractComHubUrlParams,
  handleDismissOptimisticUpdate,
  handleDismissOptimisticRollback,
} from '@utils/ComHubUtils';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';
import { clearCompanyIndicatorsCache } from '@company/hooks/useCompanyIndicators';

interface ReceivedReviewsProps {
  onShowWorkerProfile?: (data: WorkerModalParams) => void;
  onSeeReceivedReview: (review: Review) => void;
  onShowAlert?: (success: boolean, message: string) => void;
  newReviewsCount?: number;
  onCounterUpdate?: (delta: number) => void;
}

function ReceivedReviews({
  onShowWorkerProfile,
  onSeeReceivedReview,
  onShowAlert,
  newReviewsCount,
  onCounterUpdate,
}: ReceivedReviewsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchState, setSearchState] = useState<{
    searchText: string;
    dateRange: { startDate: Date | undefined; endDate: Date | undefined };
  }>();
  const [showOnlyNew, setShowOnlyNew] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // CONSTANTS FOR QUERY
  const queryClient = useQueryClient();
  const queryKeyReceivedReviews = [
    QueryKeys.ComHub.RECEIVED_REVIEWS,
    currentOffset,
    showOnlyNew,
    searchState,
  ];

  // useQuery fetch
  const {
    data: receivedReviewsResponse,
    isLoading,
    error: receivedReviewsError,
  } = useQuery({
    queryKey: queryKeyReceivedReviews,
    queryFn: () =>
      getReceivedReviews(
        currentOffset,
        DEFAULT_ITEMS_PER_PAGE,
        searchState?.searchText,
        searchState?.dateRange,
        showOnlyNew
      ),
    enabled: isInitialized, // Only run query after initialization
  });

  // useQuery update
  const { mutateAsync, isPending: isDismissing } = useMutation({
    mutationFn: dismissItemsFromNew,
    onSuccess: (_, { items }) => {
      handleDismissOptimisticUpdate(queryClient, items, 'setNewFalse');
      clearCompanyIndicatorsCache();
    },
    onError: (_, { items }) => {
      handleDismissOptimisticRollback(queryClient, items, 'setNewFalse');
    },
    onSettled: (_, __, { items }) => {
      queryClient.invalidateQueries({ queryKey: queryKeyReceivedReviews });
      // Dismiss decreases counter by number of items dismissed
      const dismissedCount = items.reduce(
        (acc, item) => acc + item.associatedIds.length,
        0
      );
      onCounterUpdate?.(-dismissedCount);
    },
  });

  // Handle query errors
  useEffect(() => {
    if (receivedReviewsError) {
      onShowAlert?.(false, receivedReviewsError.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivedReviewsError]);

  // Initialize state from URL parameters
  useEffect(() => {
    if (isInitialized) return;

    const {
      searchState: urlSearchState,
      showOnlyNew: urlShowOnlyNew,
      offset: urlOffset,
    } = extractComHubUrlParams(searchParams);

    if (urlSearchState) {
      setSearchState(urlSearchState);
    }

    if (urlShowOnlyNew !== undefined) {
      setShowOnlyNew(urlShowOnlyNew);
    }

    if (urlOffset !== undefined) {
      setCurrentOffset(urlOffset);
    }

    setIsInitialized(true);
  }, [searchParams, isInitialized]);

  const updateSearchParams = useCallback(
    (params: URLSearchParams) => {
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const handleOnSearch = useCallback(
    (search: {
      searchText: string;
      dateRange: { startDate: Date | undefined; endDate: Date | undefined };
    }) => {
      handleComHubSearch(search, setSearchState, updateSearchParams, {
        itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
        setCurrentOffset,
      });
    },
    [updateSearchParams, setCurrentOffset]
  );

  // Update URL when offset changes
  useEffect(() => {
    if (!isInitialized) return;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(ComHubQueryParamsEnum.OFFSET, currentOffset.toString());
    newSearchParams.set(
      ComHubQueryParamsEnum.MAX,
      DEFAULT_ITEMS_PER_PAGE.toString()
    );
    updateSearchParams(newSearchParams);
  }, [currentOffset, searchParams, updateSearchParams, isInitialized]);

  return (
    <>
      <FilterBar
        onSearch={handleOnSearch}
        showOnlyNew={showOnlyNew}
        onShowOnlyNew={() => {
          setShowOnlyNew((prevState) => !prevState);
          setCurrentOffset(0);
        }}
      />
      {(isLoading || isDismissing) && <ComHubSkeletonLoader />}
      {!isLoading && !isDismissing && receivedReviewsResponse ? (
        <>
          <ReceivedReviewsTable
            title={ComHubTabs.RECEIVED_REVIEWS}
            receivedReviews={receivedReviewsResponse.reviews}
            onSeeReceivedReview={onSeeReceivedReview}
            onShowWorkerProfile={onShowWorkerProfile}
            onDismissItems={(items) =>
              dismissItems(
                items,
                UserActionAssociatedTypeEnum.COMPANY_RATING,
                mutateAsync
              )
            }
            newCount={newReviewsCount}
          />
          <ThPagination
            currentOffset={receivedReviewsResponse.pagination.offset}
            onPageChange={setCurrentOffset}
            itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
            totalItems={receivedReviewsResponse.pagination.count}
          />
        </>
      ) : (
        <p className="mt-5 d-flex justify-content-center">
          {!isLoading &&
            !isDismissing &&
            'There are no received reviews for the selected period. You can change the date range or search by event, name or position.'}
        </p>
      )}
    </>
  );
}

export default ReceivedReviews;
